// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { SystemDocument, SystemDocumentType } from '@interfaces/document.interfaces';
import { AppEvent } from '@interfaces/interfaces';
import { JobTitle, UserTypes } from '@interfaces/user.interfaces';
import { EnrollmentService } from '@services/enrollment.service';
import { ErrorService } from '@services/error.service';
import { UserService } from '@services/user.service';
import { debounceTime, takeWhile } from 'rxjs';
import { SuccessModalComponent } from 'src/app/modals/success-modal/success-modal.component';
import { emailPattern, namePattern, phoneExporter, phoneImporter, usPhonePattern } from 'src/app/utils/form-utils';
import { DealershipService } from '../../../services/dealership.service';
import { formatPhone } from 'src/app/utils/format-util';
import { SettingsService } from '@services/settings.service';

@Component({
    selector: 'app-initial-dealer-setup',
    templateUrl: './initial-dealer-setup.component.html',
    styleUrls: ['./initial-dealer-setup.component.scss'],
})
export class InitialDealerSetupComponent implements OnInit {
    @Output() signal: EventEmitter<AppEvent> = new EventEmitter();
    @Input() dealerId: string;
    @Input() dealerName: string;
    @Input() dealerCode: string;
    @Input() token?: string;
    @Input() jacketId?: string;
    requiredDocs: SystemDocument[] = [];
    initialDealerSetupForm: FormGroup;
    jobTitleControl: string;
    validPassword = '';
    existingUser = false;
    isLoading = false;
    existingUserForgotPassword = false;
    existingUserIsCustomer = false;
    isComponentAlive = true;
    private logonId: string;
    oemCode: string;
    tenantSupportEmail = '';
    tenantSupportPhone = '';
    jobTitlesThatCanAcceptDpa: JobTitle[] = [];

    constructor(
        private readonly fb: FormBuilder,
        private readonly userService: UserService,
        private readonly enrollmentService: EnrollmentService,
        private readonly errorService: ErrorService,
        private readonly dialog: MatDialog,
        private readonly dealershipService: DealershipService,
        private readonly settingsService: SettingsService
    ) {}

    ngOnInit(): void {
        this.enrollmentService.getJobTitles(this.token, this.jacketId, this.dealerCode).subscribe((jobTitles) => {
            this.jobTitlesThatCanAcceptDpa = jobTitles.filter((x) => x.canAcceptDpa);
        });

        const updateOemSettings = async (oemCode) => {
            this.oemCode = oemCode;
            const data = await this.settingsService.getOemSettings(oemCode);
            this.tenantSupportEmail = data.supportEmail;
            this.tenantSupportPhone = data.supportPhone;
        };
        this.dealershipService.dealer$.pipe(takeWhile(() => this.isComponentAlive)).subscribe(async (dealer) => {
            await updateOemSettings(dealer.oemCode);
        });

        this.buildForm();

        const updateDocs = () => {
            for (const doc of this.requiredDocs) {
                if (doc.systemDocumentTypeId === SystemDocumentType.DPA) {
                    doc.pdfUrl =
                        this.oemCode === 'S' ? 'https://ezdocs123.com/agreements/TermsOfService.pdf' : 'https://app.ezdocs123.com/agreements/MiDSEULA.pdf';
                }
            }
        };

        const docsToShow = [SystemDocumentType.DPA, SystemDocumentType.TOS, SystemDocumentType.PrivacyPolicy];
        this.userService.getHelpPageSystemDocuments().subscribe((docs: SystemDocument[]) => {
            const reqDocs = docs.filter((doc) => docsToShow.includes(doc.systemDocumentTypeId));
            // ensure dpa is the first doc listed
            reqDocs.sort((doc, _) => (doc.systemDocumentTypeId == SystemDocumentType.DPA ? -1 : 1));
            this.requiredDocs = reqDocs;
            updateDocs();
        });

        this.settingsService.oemCodeEvent().subscribe(async (oemCode) => {
            await updateOemSettings(oemCode);
            updateDocs();
        });

        this.initialDealerSetupForm.controls.email.valueChanges
            .pipe(
                takeWhile(() => this.isComponentAlive),
                debounceTime(500)
            )
            .subscribe((value) => {
                value.match(/\s+$/) && this.initialDealerSetupForm.controls.email.setValue(value.trim());
            });
    }

    forgotPassword() {
        //we allow the user to reset their password directly from this context because we have already decrypted their token just like we would through regular forgot password flow.
        this.existingUserForgotPassword = true;
    }

    cancelForgotPassword() {
        this.existingUserForgotPassword = false;
    }

    resetPassword() {
        if (this.validPassword) {
            this.isLoading = true;
            this.enrollmentService.setUserPassword(this.token, this.jacketId, this.dealerCode, this.logonId, this.validPassword).subscribe((success) => {
                // make the user type in their password, just as if they never had
                this.validPassword = null;
                this.isLoading = false;

                if (success) {
                    this.existingUserForgotPassword = false;
                } else {
                    this.errorService.showError({
                        statusCode: 500,

                        message: `Please try again and if you continue to experience issues, please contact us. 
                              
                              EZ DOCS Support
                              Phone: ${this.tenantSupportPhone}
                              Email: ${this.tenantSupportEmail}`,
                    });
                }
            });
        }
    }

    buildForm() {
        this.initialDealerSetupForm = this.fb.group({
            email: ['', Validators.compose([Validators.required, Validators.maxLength(500), Validators.pattern(emailPattern)])],
            firstName: ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern(namePattern)])],
            lastName: ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern(namePattern)])],
            mobilePhone: ['', Validators.compose([Validators.required, Validators.pattern(usPhonePattern)])],
            attestSystemAdmin: [true, Validators.requiredTrue],
            termsAndConditions: [true, Validators.requiredTrue],
            privacyPolicy: [true, Validators.requiredTrue],
            jobTitle: new FormControl(this.jobTitlesThatCanAcceptDpa, Validators.required),
        });
    }

    private getCheckedSystemDocIds() {
        if (!this.requiredDocs || this.requiredDocs.length === 0) {
            return [];
        }

        const checkedSystemDocIds = [];
        if (this.initialDealerSetupForm.controls.attestSystemAdmin.value) {
            checkedSystemDocIds.push(this.requiredDocs.find((doc) => doc.systemDocumentTypeId === SystemDocumentType.DPA).currentSystemDocumentId);
        }

        if (this.initialDealerSetupForm.controls.termsAndConditions.value) {
            checkedSystemDocIds.push(this.requiredDocs.find((doc) => doc.systemDocumentTypeId === SystemDocumentType.TOS).currentSystemDocumentId);
        }

        if (this.initialDealerSetupForm.controls.privacyPolicy.value) {
            checkedSystemDocIds.push(this.requiredDocs.find((doc) => doc.systemDocumentTypeId === SystemDocumentType.PrivacyPolicy).currentSystemDocumentId);
        }

        return checkedSystemDocIds;
    }

    formValid() {
        return this.initialDealerSetupForm.valid;
    }

    save() {
        if (!this.formValid()) {
            return;
        }

        if (this.existingUserIsCustomer) {
            this.dialog.open(SuccessModalComponent, {
                width: '700px',
                data: {
                    btnText: 'Continue',
                    title: 'Invalid Email Address',
                    content:
                        'This email address is already used in the system as a customer and cannot be used for your dealer user account. Please enter a different email address.',
                },
            });
            return;
        }

        if (this.existingUser && !this.validPassword) {
            this.dialog.open(SuccessModalComponent, {
                width: '700px',
                data: {
                    btnText: 'Continue',
                    title: 'Password Required to Continue',
                    content: 'For security purposes, you must enter your valid password. Click Continue to return to the screen to enter it.',
                },
            });

            return;
        }

        const fields = this.initialDealerSetupForm.controls;
        const email = fields.email.value;
        const firstName = fields.firstName.value;
        const lastName = fields.lastName.value;
        const phone = phoneExporter(fields.mobilePhone.value);
        const attestSystemAdmin = fields.attestSystemAdmin.value;
        const jobTitle = fields.jobTitle.value.toString();

        if (!attestSystemAdmin) {
            this.dialog.open(SuccessModalComponent, {
                width: '700px',
                data: {
                    btnText: 'Continue',
                    title: 'You Must Indicate That You Are A System Administrator To Continue',
                    content: 'Please click Continue and indicate you are a System Administrator before proceeding.',
                },
            });

            return;
        }

        this.signal.emit({
            context: 'data',
            data: {
                needsNewPassword: !this.existingUser,
                email,
                firstName,
                lastName,
                phone,
                jobTitle,
                password: this.validPassword,
                checkedSystemDocuments: this.getCheckedSystemDocIds(),
            },
        });

        this.signal.emit({
            context: 'next',
            data: null,
        });

        return;
    }

    checkIfUserExistsForEmail() {
        if (this.initialDealerSetupForm.controls.email.valid) {
            this.enrollmentService
                .getUserByEmail$(this.token, this.jacketId, this.dealerCode, this.initialDealerSetupForm.controls.email.value)
                .subscribe((user) => {
                    this.logonId = user.logonId;
                    const customers = [UserTypes.Buyer, UserTypes.CoBuyer, UserTypes.Customer];
                    this.existingUserIsCustomer = customers.includes(user?.userType);
                    if (user) {
                        this.existingUser = true;

                        const phone = formatPhone(phoneImporter(user.phone));

                        this.initialDealerSetupForm.controls.email.patchValue(user.email);
                        this.initialDealerSetupForm.controls.firstName.patchValue(user.firstName);
                        this.initialDealerSetupForm.controls.lastName.patchValue(user.lastName);
                        this.initialDealerSetupForm.controls.mobilePhone.patchValue(phone);
                        this.jobTitleControl = this.jobTitlesThatCanAcceptDpa.find((x) => x.jobTitle.toLowerCase() === user.jobTitle.toLowerCase())?.jobTitle;
                    } else {
                        this.existingUser = false;
                        this.initialDealerSetupForm.controls.firstName.patchValue('');
                        this.initialDealerSetupForm.controls.lastName.patchValue('');
                        this.initialDealerSetupForm.controls.mobilePhone.patchValue('');
                        this.jobTitleControl = '';
                    }
                });
        } else if (this.existingUser === true) {
            this.existingUser = false;
            this.initialDealerSetupForm.controls.firstName.patchValue('');
            this.initialDealerSetupForm.controls.lastName.patchValue('');
            this.initialDealerSetupForm.controls.mobilePhone.patchValue('');
            this.jobTitleControl = '';
        }
    }

    cancel() {
        this.signal.emit({ context: 'cancel', data: null });
    }

    handlePasswordValid = (passwordEvent: string) => {
        this.validPassword = passwordEvent;
    };

    handlePasswordInvalid = () => {
        this.validPassword = null;
    };

    acceptTermsCallback(event: AppEvent) {
        if ('onChange' !== event.context) {
            return;
        }

        const doc: SystemDocument = event.data;
        const isChecked = doc.accepted;
        let control: AbstractControl = null;
        switch (doc.systemDocumentTypeId) {
            case SystemDocumentType.DPA: {
                control = this.initialDealerSetupForm.controls.attestSystemAdmin;
                break;
            }
            case SystemDocumentType.TOS: {
                control = this.initialDealerSetupForm.controls.termsAndConditions;
                break;
            }
            case SystemDocumentType.PrivacyPolicy: {
                control = this.initialDealerSetupForm.controls.privacyPolicy;
                break;
            }
        }

        if (control) {
            control.patchValue(isChecked);
        }
    }
}
